import { AbstractControl, UntypedFormArray, ValidationErrors } from '@angular/forms';
import { loggerFor } from '@app/utils/logger';
import { StringMap } from '@app/utils/string-map';

export type UniqueValueFunction<T> = (model: T) => any;

type ControlValue = { control: AbstractControl; value: any };

const logger = loggerFor('UniqueValidator');

/**
 * Used to validate form arrays on duplicates
 * @constructor
 * @param f
 */
export function UniqueValidatorFactory<T>(f: UniqueValueFunction<T>) {
  return function (component: AbstractControl): ValidationErrors {
    logger.debug('Called unique validator');

    const errors = {};
    const formArray = component as UntypedFormArray;

    const mapToTuple: (AbstractControl) => ControlValue = (control: AbstractControl) => {
      // Reset duplicate errors
      if (control.hasError('duplicate')) {
        // If the control has also some other error
        if (Object.keys(control.errors).length > 1) {
          const errors = control.errors || {};

          delete errors['duplicate'];

          control.setErrors(errors);
        } else {
          control.setErrors(null);
        }
      }

      return {
        control,
        value: f(control.value),
      };
    };

    const controlValueList = formArray.controls.map(mapToTuple);
    const unique: StringMap<ControlValue> = {};

    for (let entry of controlValueList) {
      if (entry.value in unique) {
        entry.control.setErrors({
          duplicate: true,
        });

        unique[entry.value].control.setErrors({
          duplicate: true,
        });

        errors['duplicates'] = true;
      } else {
        unique[entry.value] = entry;
      }
    }

    return errors;
  };
}

/**
 * Used to validate for duplicate values of the controls in form-groups found as controls in a form array.
 * Will set error on specific form control identified by `formControlName`.
 * Validator is to be set on form array control.
 *
 * Note: this is for example in use in multiline devices (IAD) form-array to check IPUI/IPEI values for duplicates.
 * @constructor
 * @param f function providing value for comparison
 * @param formControlName name of the control found in form-group of the form-array
 */
export function UniqueValidatorInFormGroupsOfFormArray<T>(
  f: UniqueValueFunction<T>,
  formControlName?: string
) {
  return function (component: AbstractControl): ValidationErrors {
    logger.debug('Called unique validator');

    const errors = {};
    const formArray = component as UntypedFormArray;

    const resetDuplicateErrors: (AbstractControl) => void = (control: AbstractControl) => {
      if (control.hasError('duplicate')) {
        // If the control has also some other error
        if (Object.keys(control.errors).length > 1) {
          const errors = control.errors || {};

          delete errors['duplicate'];

          control.setErrors(errors);
        } else {
          control.setErrors(null);
        }
      }
    };
    const mapToTuple: (AbstractControl) => ControlValue = (control: AbstractControl) => {
      // Reset duplicate errors
      resetDuplicateErrors(control);
      const formControl = control.get([formControlName]);
      if (formControl != null) {
        resetDuplicateErrors(formControl);
      }

      return {
        control,
        value: f(control.value),
      };
    };

    const controlValueList = formArray.controls.map(mapToTuple);
    const unique: StringMap<ControlValue> = {};

    for (let entry of controlValueList) {
      if (entry.value != null && entry.value !== '') {
        if (entry.value in unique) {
          entry.control.setErrors({
            duplicate: true,
          });
          const formControl = entry.control.get([formControlName]);
          if (formControl != null) {
            formControl.setErrors({
              duplicate: true,
            });
          }

          unique[entry.value].control.setErrors({
            duplicate: true,
          });

          if (unique[entry.value].control.get(formControlName) != null) {
            unique[entry.value].control.get(formControlName).setErrors({
              duplicate: true,
            });
          }

          errors['duplicates'] = true;
        } else {
          unique[entry.value] = entry;
        }
      }
    }

    return errors;
  };
}
